import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { message } from 'antd';
import { TWModal, TWButton } from '../../../components/ui';
import { formatCurrency } from '../../../utils/baseUtils';
import referralAdminApi from '../../../services/referralAdminApi';
import ReferralStatusBadge from '../../referral/components/ReferralStatusBadge';
import { parseFraudFlags, formatFraudFlag } from './fraudFlags';

dayjs.extend(utc);

const fmtDate = (value) =>
  value ? dayjs.utc(value).format('MMM D, YYYY') : '—';

const DetailRow = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm">
    <span className="text-slate-500 dark:text-slate-400">{label}</span>
    <span className="text-right font-medium text-slate-900 dark:text-slate-100">
      {children}
    </span>
  </div>
);

// reward: tıklanan satır verisi (anında gösterim). getReward gelince üzerine yazılır.
const RewardDetailModal = ({ open, reward, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!reward?.id) return;
    setLoading(true);
    try {
      const res = await referralAdminApi.getReward(reward.id);
      setDetail(res.data?.data || null);
    } catch {
      message.error('Failed to load reward detail');
    } finally {
      setLoading(false);
    }
  }, [reward?.id]);

  useEffect(() => {
    if (!open) {
      setDetail(null);
      setBalance(null);
      return;
    }
    loadDetail();
    if (reward?.beneficiarycompanyid) {
      referralAdminApi
        .companyBalance(reward.beneficiarycompanyid)
        .then((res) => setBalance(res.data?.data || null))
        .catch(() => setBalance(null));
    }
  }, [open, reward?.beneficiarycompanyid, loadDetail]);

  const r = detail || reward || {};
  const flags = parseFraudFlags(r.fraudflags);
  const canEvaluate = r.status === 0 || r.status === 5;

  const handleEvaluate = async () => {
    if (!r.referredcompanyid) return;
    setEvaluating(true);
    try {
      await referralAdminApi.evaluateActivation(r.referredcompanyid);
      message.success('Activation re-evaluated');
      await loadDetail();
      onChanged();
    } catch (err) {
      const apiMessage =
        typeof err?.response?.data === 'string'
          ? err.response.data
          : err?.response?.data?.message || 'Re-evaluation failed';
      message.error(apiMessage);
    } finally {
      setEvaluating(false);
    }
  };

  return (
    <TWModal
      open={open}
      onClose={onClose}
      title="Reward detail"
      size="lg"
      showCloseButton
      footer={
        <div className="flex justify-end gap-2">
          {canEvaluate ? (
            <TWButton
              variant="default"
              label={evaluating ? 'Evaluating…' : 'Re-evaluate activation'}
              loading={evaluating}
              onClick={handleEvaluate}
            />
          ) : null}
          <TWButton variant="primary" label="Close" onClick={onClose} />
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-md border border-slate-200 p-3 dark:border-slate-700">
          <DetailRow label="Referrer">
            {r.referrercompanyname || '—'}
          </DetailRow>
          <DetailRow label="Referred">
            {r.referredcompanyname || '—'}
          </DetailRow>
          <DetailRow label="Beneficiary">
            {r.beneficiarycompanyname || '—'}
          </DetailRow>
          <DetailRow label="Reward amount">
            {formatCurrency(r.rewardamount)}
          </DetailRow>
          <DetailRow label="Current balance">
            {formatCurrency(r.currentbalance)}
          </DetailRow>
          <DetailRow label="Status">
            <ReferralStatusBadge
              kind="status"
              code={r.status}
              fallbackLabel={r.statusname}
            />
          </DetailRow>
          <DetailRow label="Expiration (days)">
            {r.expirationdays ?? '—'}
          </DetailRow>
          <DetailRow label="Created">{fmtDate(r.createdate)}</DetailRow>
          <DetailRow label="Activated">{fmtDate(r.activatedat)}</DetailRow>
          <DetailRow label="Expires">{fmtDate(r.expiresat)}</DetailRow>
          {r.revokereason ? (
            <DetailRow label="Revoke reason">{r.revokereason}</DetailRow>
          ) : null}
        </div>

        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            Fraud flags
          </p>
          {flags.length === 0 ? (
            <p className="text-sm text-slate-400 dark:text-slate-500">None</p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {flags.map((flag) => (
                <span
                  key={flag}
                  className="rounded bg-rose-100 px-1.5 py-0.5 text-[0.65rem] font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                >
                  {formatFraudFlag(flag)}
                </span>
              ))}
            </div>
          )}
        </div>

        {balance ? (
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Beneficiary referral balance
            </p>
            <div className="rounded-md border border-slate-200 p-3 dark:border-slate-700">
              <DetailRow label="Available">
                {balance.availablebalance != null
                  ? formatCurrency(balance.availablebalance)
                  : '—'}
              </DetailRow>
              <DetailRow label="Earned">
                {balance.totalearned != null
                  ? formatCurrency(balance.totalearned)
                  : '—'}
              </DetailRow>
              <DetailRow label="Consumed">
                {balance.totalconsumed != null
                  ? formatCurrency(balance.totalconsumed)
                  : '—'}
              </DetailRow>
              <DetailRow label="Expired">
                {balance.totalexpired != null
                  ? formatCurrency(balance.totalexpired)
                  : '—'}
              </DetailRow>
            </div>
          </div>
        ) : null}

        {loading ? (
          <p className="text-xs text-slate-400 dark:text-slate-500">
            Refreshing…
          </p>
        ) : null}
      </div>
    </TWModal>
  );
};

export default RewardDetailModal;
